import type { ComponentProps } from 'react';
import React, { useId } from 'react';
import styled from '@emotion/styled';
import { QRCodeSVG } from 'qrcode.react';
import { borderRadius, boxShadow, space } from 'styled-system';

import { Flex } from 'common/components/Flex';
import { BodyRegularBR1, HeadlineH4 } from 'common/components/Typography';
import { useLocalization } from 'common/providers/LocaleProvider';

interface QRCodeSectionProps {
  homeClubId: string;
  withTitle?: boolean;
}

const QR_CODE_SIZE = 180;

const Container = styled(Flex)``;

const containerStyles: ComponentProps<typeof Container> = {
  pt: 'sp_16',
  pb: ['sp_16', null, null, 'sp_20'],
  px: 'sp_16',
  flexDirection: 'column',
  alignItems: 'center',
};

const QRCodeContainer = styled.div`
  ${space};
  ${boxShadow};
  ${borderRadius};
`;

const qrCodeContainerStyles = {
  p: 'sp_16',
  boxShadow: '0px 1px 15px 0px rgba(0, 0, 0, 0.20)',
  borderRadius: 'large',
};

const QRCodeSection = ({ homeClubId, withTitle = false }: QRCodeSectionProps) => {
  const { t } = useLocalization();
  const headingId = useId();

  return (
    <Container
      {...containerStyles}
      as="section"
      {...(withTitle
        ? { 'aria-labelledby': headingId }
        : { 'aria-label': t('h24_hc_qrcode_title') })}
    >
      {withTitle && (
        <HeadlineH4 as="h1" textAlign="center" id={headingId}>
          {t('h24_hc_qrcode_title')}
        </HeadlineH4>
      )}
      <QRCodeContainer {...qrCodeContainerStyles}>
        <QRCodeSVG size={QR_CODE_SIZE} value={homeClubId} data-testid="qr-code" />
      </QRCodeContainer>
      <BodyRegularBR1 mt="sp_12">{`${t('h24_hc_id')} ${homeClubId}`}</BodyRegularBR1>
      <BodyRegularBR1 mt="sp_20" color="sys.neutral.text.strong" textAlign="center">
        {t('h24_hc_qrcode_description')}
      </BodyRegularBR1>
    </Container>
  );
};

export default QRCodeSection;
